import { Alert, Chip, Stack, Typography } from "@mui/material";

import { memo } from "react";

import type { SignupProvisioningContent } from "@/api/signup/provisioning";
import { useTranslation } from "next-i18next";

const normalizeText = (value?: string | null): string => {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
};

const HomeProvisioningBanner = memo(function HomeProvisioningBanner({
  content
}: {
  content: SignupProvisioningContent | null;
}) {
  const { t } = useTranslation("common");

  if (!content || content.access_ready !== true || content.ready === true) {
    return null;
  }

  const normalizedStatus = normalizeText(content.status || "in progress").replace(/_/g, " ");
  const statusLabel = t(normalizedStatus, { defaultValue: normalizedStatus });
  const currentStep = normalizeText(content.current_step).replace(/_/g, " ");
  const message = normalizeText(content.message) || t("The app shell is ready while optional setup keeps running.");

  return (
    <Alert
      severity="info"
      variant="outlined"
      sx={{
        mb: 2,
        alignItems: "flex-start"
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t("Workspace setup is still finishing")}
          </Typography>
          <Chip size="small" label={statusLabel || t("in progress")} variant="outlined" />
        </Stack>
        <Typography variant="body2">{message}</Typography>
        {currentStep ? (
          <Typography variant="caption" color="text.secondary">
            {t("Current step:")} {currentStep}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  );
});

HomeProvisioningBanner.displayName = "HomeProvisioningBanner";

export default HomeProvisioningBanner;
